import { IMat2, Mat2 } from "@base/math/Mat2";
import { IVec2, Vec2 } from "@base/math/Vec2";

export interface IOBB2 {
  center: IVec2;
  extent: IVec2;
  rotation: IMat2;
}

function applyMatrix2(v: IVec2, m: IMat2): IVec2 {
  const { x, y } = v;

  return {
    x: m[0] * x + m[2] * y,
    y: m[1] * x + m[3] * y,
  };
}

export class OBB2 {
  static calcOBB(hull: IVec2[]): IOBB2 {
    let minArea = Infinity;
    let bestOBB: IOBB2 = { center: { x: 0, y: 0 }, extent: { x: 0, y: 0 }, rotation: Mat2.IDENTITY };

    const n = hull.length;
    for (let i = 0; i < n; i++) {
      // const t = 0;
      // for (let i = t; i < t + 1; i++) {
      const p1 = hull[i];
      const p12 = Vec2.sub(hull[(i + 1) % n], p1);
      const edgeAngle = Math.atan2(p12.y, p12.x);
      const rotation = Mat2.createRotation(-edgeAngle);
      const invRotation = Mat2.inverse(rotation)!;

      const min = Vec2.from(Infinity, Infinity);
      const max = Vec2.from(-Infinity, -Infinity);
      for (const p of hull) {
        const rotated = applyMatrix2(Vec2.sub(p, p1), invRotation);
        min.x = Math.min(min.x, rotated.x);
        min.y = Math.min(min.y, rotated.y);
        max.x = Math.max(max.x, rotated.x);
        max.y = Math.max(max.y, rotated.y);
      }

      const size = Vec2.sub(max, min);
      const area = size.x * size.y;

      if (area < minArea) {
        minArea = area;
        const center = Vec2.add(applyMatrix2(Vec2.center([min, max]), rotation), p1);
        const extent = { x: size.x / 2, y: size.y / 2 };
        bestOBB = { center, extent, rotation };
      }
    }

    return bestOBB;
  }

  static calcCornerPoints(obb: IOBB2): IVec2[] {
    const { center, extent, rotation } = obb;
    const corners: IVec2[] = [];

    const halfWidth = extent.x;
    const halfHeight = extent.y;

    const cornerOffsets: IVec2[] = [
      { x: -halfWidth, y: -halfHeight },
      { x: halfWidth, y: -halfHeight },
      { x: halfWidth, y: halfHeight },
      { x: -halfWidth, y: halfHeight },
    ];

    for (const offset of cornerOffsets) {
      const corner = applyMatrix2(offset, rotation);
      corners.push(Vec2.add(corner, center));
    }

    return corners;
  }

  static calcArea(obb: IOBB2): number {
    const { extent } = obb;
    return extent.x * extent.y * 4;
  }
}

// calculate the convex hull using the Graham scan algorithm
export class ConvexHull2 {
  static calc(points: IVec2[]): IVec2[] {
    if (points.length < 3) return points;

    // Find the point with the lowest y-coordinate (and leftmost if tie)
    let start = points[0];
    for (const point of points) {
      if (point.y < start.y || (point.y === start.y && point.x < start.x)) {
        start = point;
      }
    }

    // Sort points by polar angle with respect to the start point
    points.sort((a, b) => {
      const angleA = Math.atan2(a.y - start.y, a.x - start.x);
      const angleB = Math.atan2(b.y - start.y, b.x - start.x);
      if (angleA === angleB) {
        return Math.hypot(start.x - a.x, start.y - a.y) - Math.hypot(start.x - b.x, start.y - b.y);
      }
      return angleA - angleB;
    });

    // Initialize the hull with the start point
    const hull: IVec2[] = [start];

    for (const point of points) {
      while (hull.length > 1 && crossProduct(hull[hull.length - 2], hull[hull.length - 1], point) <= 0) {
        hull.pop();
      }
      hull.push(point);
    }

    return hull;
  }
}

function crossProduct(o: IVec2, a: IVec2, b: IVec2): number {
  return (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
}